import { alpha, Box, Grid, styled } from '@mui/material';
import { DataGridPro, gridClasses, useGridApiRef } from '@mui/x-data-grid-pro';
import { DatePicker } from '@mui/x-date-pickers-pro';
import {
  DefaultError,
  InfiniteData,
  useInfiniteQuery,
  useQueries,
} from '@tanstack/react-query';
import { format, isValid, parse, subYears } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { SingleOperators } from 'common/src/query-params/operators';
import {
  IBigQueryBillDto,
  IBigQueryLineItemDto,
} from 'common/src/types/bigQueryBIllDto';
import { IAccount, IMeter, IPage, IUtility } from 'common/src/types/interfaces';

import { getAccounts } from 'portal/src/api/AccountsService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import { getMeters } from 'portal/src/api/MetersService';
import { getBigQuery } from 'portal/src/api/BiqQueryService';
import { getUtilities } from 'portal/src/api/UtilitiesService';
import { numberFormatter } from 'portal/src/utils/numberFormatter';
import { dateOnlyFormat } from 'portal/src/validators/dateValidator';

import {
  getBillColumns,
  getLineItemColumns,
} from './BillReportDashboard.columns';
import BillReportsToolbar from './BillReportsToolbar';

import FormController from '../common/forms/FormController';
import DashboardAutoCompleteFilterCard from '../dashboard/DashboardAutoCompleteFilterCard';
import DashboardFilterCard from '../dashboard/DashboardFilterCard';
import DashboardHighlightCard from '../dashboard/DashboardHighlightCard';

const LIMIT = 20000;
const ODD_OPACITY = 0.1;

const today = new Date();
const oneYearAgo = subYears(today, 1);
const DEFAULT_RANGE_DATE = {
  id: 1,
  field: 'billStartDate',
  operator: SingleOperators.BETWEEN,
  value: [oneYearAgo, today],
};

const STRIPPED_GRID = styled(DataGridPro)(({ theme }) => ({
  background: 'white',
  '& .MuiDataGrid-virtualScroller': {
    minHeight: '45vh',
    maxHeight: '93vh',
  },
  '& .stripped-cell': {
    fontSize: '11.2px',
    borderColor: theme.palette.grey[300],
  },
  '& .blue-header': {
    background: 'rgb(14, 36, 60)',
    color: 'rgb(245, 245, 245)',
    fontSize: '12px',
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
  },
  '& .MuiDataGrid-treeDataGroupingCellToggle': {
    marginRight: 0,
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    '&:hover': {
      backgroundColor: theme.palette.grey[300],
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      '&:hover': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
            theme.palette.action.selectedOpacity +
            theme.palette.action.hoverOpacity,
        ),
      },
    },
  },
}));

const INFINITY_FETCH_OPTIONS = {
  initialPageParam: 0,
  getNextPageParam: (lastPage, allPages) => {
    if (!lastPage || lastPage.length < LIMIT) {
      return undefined;
    }

    return allPages.length * LIMIT;
  },
  initialData: { pages: [], pageParams: [] },
};

const getLineItemsData = (
  lineItemsPages: InfiniteData<IBigQueryLineItemDto[], unknown>,
) => {
  const seenLineItems = new Set<string>();

  return lineItemsPages.pages.flatMap((page) =>
    page.flatMap((li) => {
      const lineItemKey = `${li.billId}:${li.lineItemId}`;

      if (seenLineItems.has(lineItemKey)) {
        return [];
      }

      seenLineItems.add(lineItemKey);

      return {
        billId: li.billId,
        id: li.lineItemId,
        tree: [li.billId, li.lineItemId],
        endDate: li.lineItemEndDate ?? '',
        lineItemCost: li.lineItemCost,
        lineItemUnit: li.lineItemUnit,
        lineItemUsage: li.lineItemUsage,
        lineItemType: li.lineItemType,
        startDate: li.lineItemStartDate ?? '',
        meterId: li.meterId,
        rowType: 'child',
      };
    }),
  );
};

export const BillReportDashboard = () => {
  const tableRef = useGridApiRef();

  const [isExporting, setIsExporting] = useState(false);
  const [filterModel, setFilterModel] = useState({
    items: [DEFAULT_RANGE_DATE],
  });

  const { control } = useForm({
    defaultValues: {
      billStartDate: DEFAULT_RANGE_DATE.value,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const billStartDate = useWatch({ control, name: 'billStartDate' });

  const billStartDateValues = useMemo(
    () =>
      billStartDate?.map((date: Date | null) =>
        date && isValid(date) ? format(date, dateOnlyFormat) : null,
      ) ?? [null, null],
    [billStartDate],
  );
  const billStartDateQueryKey = billStartDateValues
    .map((value) => value ?? 'null')
    .join('|');
  const hasStartAndEndValues = billStartDateValues.every((value) => !!value);
  const hasTwoNullValues = billStartDateValues.every((value) => value === null);
  const hasValidStartDateRange = hasStartAndEndValues || hasTwoNullValues;

  const [
    { data: accountsData },
    { data: metersData },
    { data: utilitiesData },
  ] = useQueries({
    queries: [
      {
        queryKey: [QUERY_KEYS.accounts],
        queryFn: async () => await getAccounts(),
        initialData: { data: [] } as IPage<IAccount>,
      },
      {
        queryKey: [QUERY_KEYS.meters],
        queryFn: async () => await getMeters(),
        initialData: { data: [] } as IPage<IMeter>,
      },
      {
        queryKey: [QUERY_KEYS.utilities],
        queryFn: async () => await getUtilities(),
        initialData: { data: [] } as IPage<IUtility>,
      },
    ],
  });

  const {
    data: billsData,
    fetchNextPage: fetchNextPageBills,
    hasNextPage: hasNextPageBills,
    isPending: isPendingBills,
    isFetching: isFetchingBills,
    isFetchingNextPage: isFetchingNextPageBills,
  } = useInfiniteQuery<
    IBigQueryBillDto[],
    DefaultError,
    InfiniteData<IBigQueryBillDto[]>,
    ['billsDataSetBQ', string],
    number
  >({
    ...INFINITY_FETCH_OPTIONS,
    queryKey: ['billsDataSetBQ', billStartDateQueryKey],
    queryFn: async ({ pageParam = 0 }) => {
      const page = await getBigQuery<IBigQueryBillDto[]>('bills', {
        limit: LIMIT,
        offset: pageParam,
        sort: 'billId',
        filters: hasStartAndEndValues
          ? filterModel.items
              .filter(({ field }) => field === 'billStartDate')
              .map((item) => ({
                ...item,
                value: billStartDateValues as string[],
              }))
          : [],
      });
      return page;
    },
    enabled: hasValidStartDateRange,
  });

  const {
    data: lineItemsPages,
    fetchNextPage: fetchNextPageLineItems,
    hasNextPage: hasNextPageLineItems,
    isFetchingNextPage: isFetchingNextPageLineItems,
    isPending: isPendingLineItems,
    isFetching: isFetchingLineItems,
  } = useInfiniteQuery<
    IBigQueryLineItemDto[],
    DefaultError,
    InfiniteData<IBigQueryLineItemDto[]>,
    ['lineItemsDataSetBQ', string],
    number
  >({
    ...INFINITY_FETCH_OPTIONS,
    queryKey: ['lineItemsDataSetBQ', billStartDateQueryKey],
    queryFn: async ({ pageParam = 0 }) => {
      const lineItems = await getBigQuery<IBigQueryLineItemDto[]>(
        'line_items',
        {
          limit: LIMIT,
          offset: pageParam,
          sort: 'lineItemId',
          filters: hasStartAndEndValues
            ? filterModel.items
                .filter(({ field }) => field === 'billStartDate')
                .map((item) => ({
                  ...item,
                  field: 'lineItemStartDate',
                  value: billStartDateValues as string[],
                }))
            : [],
        },
      );
      return lineItems;
    },
    enabled:
      hasValidStartDateRange &&
      !isFetchingBills &&
      !isPendingBills &&
      !hasNextPageBills &&
      billsData.pageParams.length > 0,
  });

  const isLoadingBills = isFetchingBills || isPendingBills;
  const isInitialBillsLoading = isPendingBills && billsData.pages.length === 0;
  const isBillsLoadingUntilComplete =
    isLoadingBills || isFetchingNextPageBills || hasNextPageBills;
  const isLoadingLineItems = isPendingLineItems || isFetchingLineItems;
  const hasStartedLineItemsFetch = lineItemsPages.pageParams.length > 0;
  const shouldBridgeToLineItemsLoading =
    !isLoadingBills &&
    !hasStartedLineItemsFetch &&
    !hasNextPageBills &&
    billsData.pageParams.length > 0;
  const showIsLoadingLineItems =
    !isLoadingBills &&
    (isLoadingLineItems ||
      isFetchingNextPageLineItems ||
      hasNextPageLineItems ||
      shouldBridgeToLineItemsLoading);

  const billsRows = useMemo(
    () =>
      billsData.pages.flat().map((bill) => ({
        id: bill.billId,
        tree: [bill.billId],
        accountId: bill.accountId,
        billAmount: bill.billAmount,
        billDate: bill.billDate,
        billNumber: bill.billNumber,
        calendarBudgetPeriod: bill.calendarBudgetPeriod ?? '',
        budgetPeriodYear: bill.budgetPeriodYear ?? '',
        billDueDate: bill.billDueDate ?? '',
        billEstimated: bill.billEstimated,
        endDate: bill.billEndDate ?? '',
        notes: bill.notes,
        startDate: bill.billStartDate,
        rowType: 'group',
      })),
    [billsData.pages],
  );

  const lineItemsData = useMemo(() => {
    return getLineItemsData(lineItemsPages);
  }, [lineItemsPages]);

  // Compute totals directly from billsData applying the same client-side
  // filters the DataGrid uses (account, utility, year, estimated).
  // The date range filter is server-side so we skip it here.
  const computedTotals = useMemo(() => {
    if (isInitialBillsLoading) {
      return { totalCost: 0, totalBills: 0 };
    }

    const allBills = billsData.pages.flat();

    const accountFilter = filterModel.items.find(
      ({ field }) => field === 'accountId',
    );
    const utilityFilter = filterModel.items.find(
      ({ field }) => field === 'utility',
    );
    const yearFilter = filterModel.items.find(
      ({ field }) => field === 'budgetPeriodYear',
    );
    const estimatedFilter = filterModel.items.find(
      ({ field }) => field === 'billEstimated',
    );

    const filtered = allBills.filter((bill) => {
      if (
        (accountFilter?.value as any[])?.length &&
        !(accountFilter.value as any[]).includes(bill.accountId)
      ) {
        return false;
      }

      if ((utilityFilter?.value as any[])?.length) {
        const account = accountsData.data.find(
          ({ id }) => id === bill.accountId,
        );
        if (
          !account ||
          !(utilityFilter.value as any[]).includes(account.utility.id)
        ) {
          return false;
        }
      }

      if (yearFilter?.value) {
        const year = String(bill.budgetPeriodYear ?? '');
        if (!year.includes(String(yearFilter.value))) {
          return false;
        }
      }

      if ((estimatedFilter?.value as any[])?.length) {
        const label = bill.billEstimated ? 'Yes' : 'No';
        if (!(estimatedFilter.value as any[]).includes(label)) {
          return false;
        }
      }

      return true;
    });

    return {
      totalCost: filtered.reduce(
        (acc, bill) => acc + (bill.billAmount ?? 0),
        0,
      ),
      totalBills: filtered.length,
    };
  }, [isInitialBillsLoading, billsData.pages, filterModel, accountsData.data]);

  const billsCol = useMemo(
    () => getBillColumns(accountsData.data, utilitiesData.data),
    [accountsData.data, utilitiesData.data],
  );

  const lineItemsCol = useMemo(
    () => getLineItemColumns(metersData.data),
    [metersData.data],
  );

  const allColumns = useMemo(
    () =>
      [...billsCol, ...lineItemsCol].map((column) => ({
        cellClassName: 'stripped-cell',
        headerClassName: 'blue-header',
        width: 100,
        ...column,
      })),
    [billsCol, lineItemsCol],
  );

  // Auto-paginate bills
  useEffect(() => {
    if (hasNextPageBills && !isFetchingNextPageBills) {
      fetchNextPageBills();
    }
  }, [hasNextPageBills, isFetchingNextPageBills, fetchNextPageBills]);

  // Auto-paginate line items
  useEffect(() => {
    if (hasNextPageLineItems && !isFetchingNextPageLineItems) {
      fetchNextPageLineItems();
    }
  }, [
    hasNextPageLineItems,
    isFetchingNextPageLineItems,
    fetchNextPageLineItems,
  ]);

  return (
    <Box
      sx={{ margin: 1.5 }}
      justifyContent="space-between"
      style={{ maxHeight: '100vh' }}
    >
      <Grid container spacing={2} paddingBottom={1.5}>
        <Grid size={3}>
          <DashboardAutoCompleteFilterCard
            title="Filter on Account Number"
            autocompleteProps={{
              disabled: isLoadingBills,
              filters: { field: 'accountId', operator: 'isAnyOf' },
              setFilter: setFilterModel,
              options: accountsData.data.map(({ id, number }) => ({
                value: id,
                label: number,
              })),
            }}
          />
        </Grid>
        <Grid size={3}>
          <DashboardAutoCompleteFilterCard
            title="Filter on Utility"
            autocompleteProps={{
              disabled: isLoadingBills,
              filters: { field: 'utility', operator: 'isAnyOf' },
              setFilter: setFilterModel,
              options: utilitiesData.data.map(({ id, name }) => ({
                value: id,
                label: name,
              })),
            }}
          />
        </Grid>
        <Grid size={3}>
          <DashboardFilterCard title="Filter on Year">
            <DatePicker
              disabled={isLoadingBills}
              views={['year']}
              openTo="year"
              onChange={(value: Date) => {
                setFilterModel((prev) => {
                  const items = prev.items.filter(
                    ({ field }) => field !== 'budgetPeriodYear',
                  );
                  value &&
                    items.push({
                      field: 'budgetPeriodYear',
                      operator: 'contains' as any,
                      id: Math.random(),
                      value: (value.getFullYear() + '') as any,
                    });
                  return { items };
                });
              }}
              slotProps={{
                field: { clearable: true },
                textField: {
                  size: 'small',
                  sx: { width: '100%' },
                },
              }}
            />
          </DashboardFilterCard>
        </Grid>
        <Grid size={3}>
          <DashboardAutoCompleteFilterCard
            title="Filter on Estimated"
            autocompleteProps={{
              disabled: isLoadingBills,
              filters: { field: 'billEstimated', operator: 'isAnyOf' },
              setFilter: setFilterModel,
              options: [
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
              ],
            }}
          />
        </Grid>
        <Grid size={6}>
          <DashboardFilterCard title="Filter on Start Date Range">
            <FormController
              control={control}
              fieldConf={{
                type: 'dateRangePicker',
                name: 'billStartDate',
                label: 'Select',
                value: 'Not allowed dates range',
                placeholder: 'Start Date Range',
                required: false,
                slotProps: {
                  field: { clearable: true },
                  textField: { size: 'small' },
                },
                validate: (
                  value: [Date | null, Date | null] | null | undefined,
                ) => {
                  const [start, end] = value ?? [null, null];
                  const hasStart = !!start;
                  const hasEnd = !!end;
                  const hasBoth = hasStart && hasEnd;
                  const hasNeither = !hasStart && !hasEnd;

                  if (hasStart && !isValid(start)) {
                    return 'Start date is invalid.';
                  }

                  if (hasEnd && !isValid(end)) {
                    return 'End date is invalid.';
                  }

                  if ((hasStart && !hasEnd) || (!hasStart && hasEnd)) {
                    return 'Please provide both start and end dates, or leave both empty.';
                  }

                  if (hasBoth && end.getTime() <= start.getTime()) {
                    return 'End date must be after start date.';
                  }

                  return hasBoth || hasNeither;
                },
              }}
            />
          </DashboardFilterCard>
        </Grid>
        <Grid size={3}>
          <DashboardHighlightCard
            cardPrompt="Total Costs"
            isLoading={isLoadingBills}
            value={numberFormatter(computedTotals.totalCost)}
          />
        </Grid>
        <Grid size={3}>
          <DashboardHighlightCard
            cardPrompt="Total Bills"
            isLoading={isLoadingBills}
            value={numberFormatter(computedTotals.totalBills, {})}
          />
        </Grid>
      </Grid>
      <Box sx={{ height: 600, mb: 2 }}>
        <STRIPPED_GRID
          apiRef={tableRef}
          {...{
            averageChildren: 100,
            groupingField: 'tree',
            maxDepth: 2,
            rowHeight: 30,
          }}
          disableChildrenFiltering
          disableColumnFilter
          disableColumnMenu
          treeData
          rows={
            !isBillsLoadingUntilComplete ? [...billsRows, ...lineItemsData] : []
          }
          columns={allColumns}
          columnHeaderHeight={32}
          filterModel={filterModel}
          loading={isBillsLoadingUntilComplete}
          showCellVerticalBorder
          getTreeDataPath={(row) => row.tree}
          groupingColDef={{
            headerName: 'Bill Date',
            headerClassName: 'blue-header',
            cellClassName: 'stripped-cell',
            sortable: true,
            sortComparator: (v1, v2) =>
              new Date(v1).getTime() - new Date(v2).getTime(),
            valueFormatter: (value) =>
              isValid(value) ? format(value, 'P') : '',
            valueGetter: (_, row) => {
              if (!row.billDate) return '';
              const parsedDate = parse(
                row.billDate,
                dateOnlyFormat,
                new Date(),
              );
              return isValid(parsedDate) && row.tree?.length === 1
                ? parsedDate
                : '';
            },
            width: 130,
          }}
          getRowClassName={({ indexRelativeToCurrentPage }) =>
            indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          slots={{ toolbar: BillReportsToolbar }}
          showToolbar
          slotProps={{
            toolbar: {
              isLoadingBills,
              showIsLoadingLineItems,
              isExporting,
              setIsExporting,
            },
            footer: { sx: { display: 'none' } },
          }}
          sx={{
            '& .MuiDataGrid-columnHeader .MuiDataGrid-sortButton': {
              backgroundColor: 'unset',
            },
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: '__tree_data_group__', sort: 'desc' }],
            },
          }}
        />
      </Box>
    </Box>
  );
};
